/*
 * Réglages NVS d'une cible ou d'une carabine, par Bluetooth — environnement technicien.
 *
 * Deux clés se posent ici, une fois par carte, à la mise en service :
 *
 *   HARDWARE  — 2026 ou 2027 tout-en-un. Ce modèle n'est plus deviné : trois tentatives de
 *               détection électrique ont été faites, deux se sont révélées fausses en essai.
 *               Absence de clé = 2026, sur les deux appareils.
 *   BATTERIE  — cible seulement. 0 déclare une cible 2027 qu'on fait volontairement tourner sur
 *               câble, sans accu : ni jauge, ni charge, ni veille « batterie faible ». Absence de
 *               clé = il y a une batterie, c'est le cas normal.
 *
 * Le raisonnement complet est dans bureauEtude/V2027/DETECTION_MATERIEL.md.
 *
 * Les deux appareils parlent le même vocabulaire, compilé depuis un seul fichier partagé
 * (telecommandedevKit/src/commandesNvs.h), sur les mêmes UUID. Ce qui diffère, c'est QUAND on
 * peut leur parler :
 *
 *   cible     — en permanence : la commande générique du service de jeu, celle qui traite déjà
 *               RESET et MASTER, a reçu ce vocabulaire ;
 *   carabine  — UNIQUEMENT EN ATTENTE OTA (écran « Pair/OTA ») : c'est le seul moment où elle
 *               allume son Bluetooth. Elle n'annonce alors que le service OTA, d'où un filtre de
 *               recherche différent.
 *
 * Aucune caractéristique Bluetooth n'a été ajoutée (demande de Louis) : on écrit dans la commande
 * générique et l'appareil y dépose sa réponse — seule la propriété « lecture » lui a été donnée.
 * La caractéristique de score de la cible n'est pas détournée : elle reste le score.
 */

import React, { useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonText,
} from "@ionic/react";
import { BleClient, numberToUUID } from "@capacitor-community/bluetooth-le";

// Service qui porte la commande générique, sur les deux appareils.
const SERVICE_JEU = "4fafc201-1fb5-459e-8fcc-c5c9c331914b";
// Écriture ET lecture : RESET, MASTER (cible), HARDWARE, BATTERIE (cible), GET, NVS.
const CARAC_CMD = "beb5483e-36e1-4688-b7f5-ea07361b2003";
// Le seul service que la carabine annonce en attente OTA : c'est par lui qu'on la trouve.
const SERVICE_OTA = "00008018-0000-1000-8000-00805f9b34fb";

// Service d'information sur l'appareil, standard Bluetooth : il porte la version de firmware.
const SERVICE_DIS = numberToUUID(0x180a);
const CARAC_VERSION_FW = numberToUUID(0x2a26);

type Appareil = "cible" | "carabine";
type Modele = "2026" | "2027";
type Batterie = "1" | "0";

const texteVersBuffer = (t: string): DataView => {
  const octets = new TextEncoder().encode(t);
  return new DataView(octets.buffer, octets.byteOffset, octets.byteLength);
};

const bufferVersTexte = (v: DataView): string =>
  new TextDecoder().decode(new Uint8Array(v.buffer, v.byteOffset, v.byteLength));

const NvsTechnicien: React.FC = () => {
  const [type, setType] = useState<Appareil>("cible");
  const [appareil, setAppareil] = useState<{ deviceId: string; name?: string } | null>(null);
  const [modele, setModele] = useState<Modele>("2027");
  const [batterie, setBatterie] = useState<Batterie>("1");
  const [inventaire, setInventaire] = useState<string>("");
  const [versionFw, setVersionFw] = useState<string>("");
  const [hardwareLu, setHardwareLu] = useState<string>("");
  const [batterieLue, setBatterieLue] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [erreur, setErreur] = useState<boolean>(false);
  const [occupe, setOccupe] = useState<boolean>(false);

  const nomType = type === "cible" ? "la cible" : "la carabine";

  const dire = (t: string, estErreur = false) => {
    setMessage(t);
    setErreur(estErreur);
  };

  const oublier = () => {
    setAppareil(null);
    setInventaire("");
    setVersionFw("");
    setHardwareLu("");
    setBatterieLue("");
  };

  /* Lit une clé par la commande générique : on y écrit GET<clé>, l'appareil y dépose
     « clé=valeur », on relit la même caractéristique. La courte attente lui laisse le temps de
     déposer, et évite de relire l'ancienne valeur. */
  const lireCle = async (deviceId: string, cle: string, poser: (v: string) => void) => {
    try {
      await BleClient.write(deviceId, SERVICE_JEU, CARAC_CMD, texteVersBuffer(`GET${cle}`));
      await new Promise((r) => setTimeout(r, 250));
      const v = await BleClient.read(deviceId, SERVICE_JEU, CARAC_CMD);
      const brut = bufferVersTexte(v);
      poser(brut.startsWith(`${cle}=`) ? brut.slice(cle.length + 1) : brut);
    } catch {
      poser("(illisible)");
    }
  };

  const connecter = async () => {
    setOccupe(true);
    try {
      await BleClient.initialize();
      // La cible annonce son service de jeu ; la carabine en attente OTA n'annonce que le
      // service OTA, mais porte bien le service de jeu dans sa table GATT.
      const d =
        type === "cible"
          ? await BleClient.requestDevice({ services: [SERVICE_JEU], optionalServices: [SERVICE_DIS] })
          : await BleClient.requestDevice({
              services: [SERVICE_OTA],
              optionalServices: [SERVICE_JEU, SERVICE_DIS],
            });
      await BleClient.connect(d.deviceId, () => {
        oublier();
        dire(`${type === "cible" ? "Cible" : "Carabine"} déconnectée.`);
      });
      setAppareil({ deviceId: d.deviceId, name: d.name });
      dire(`Connecté à ${d.name ?? d.deviceId}.`);

      try {
        const v = await BleClient.read(d.deviceId, SERVICE_DIS, CARAC_VERSION_FW, { timeout: 800 });
        setVersionFw(bufferVersTexte(v));
      } catch {
        setVersionFw("(non publiée)");
      }

      // Valeurs actuelles, pour savoir ce qu'on change avant de le changer.
      await lireCle(d.deviceId, "HARDWARE", setHardwareLu);
      if (type === "cible") {
        await lireCle(d.deviceId, "BATTERIE", setBatterieLue);
      }
    } catch (e: any) {
      dire(
        type === "carabine"
          ? `Connexion impossible : ${e?.message ?? e}. La carabine est-elle bien en attente OTA (écran « Pair/OTA ») ? Une carabine 3.07 ou plus ancienne n'a pas ces réglages.`
          : `Connexion impossible : ${e?.message ?? e}`,
        true
      );
    }
    setOccupe(false);
  };

  const deconnecter = async () => {
    if (!appareil) return;
    try {
      await BleClient.disconnect(appareil.deviceId);
    } catch {
      /* déjà partie */
    }
    oublier();
    dire("Déconnecté.");
  };

  const envoyer = async (commande: string) => {
    if (!appareil) return;
    await BleClient.write(appareil.deviceId, SERVICE_JEU, CARAC_CMD, texteVersBuffer(commande));
  };

  // L'appareil redémarre juste après avoir gravé la clé : ces réglages sont lus une seule fois
  // au démarrage et commandent le brochage, ils ne peuvent pas changer à chaud. La déconnexion qui
  // suit est donc normale, et c'est elle qui confirme que l'écriture a eu lieu. Une carabine
  // redémarre en jeu, hors de l'attente OTA.
  const graver = async (commande: string, libelle: string) => {
    setOccupe(true);
    try {
      await envoyer(commande);
      dire(`${libelle} gravé. ${type === "cible" ? "La cible" : "La carabine"} redémarre, la liaison va tomber.`);
      oublier();
    } catch (e: any) {
      dire(`Écriture refusée : ${e?.message ?? e}`, true);
    }
    setOccupe(false);
  };

  const lireInventaire = async () => {
    if (!appareil) return;
    setOccupe(true);
    try {
      await envoyer("NVS");
      await new Promise((r) => setTimeout(r, 300));
      const v = await BleClient.read(appareil.deviceId, SERVICE_JEU, CARAC_CMD);
      setInventaire(bufferVersTexte(v));
      dire("Inventaire relu.");
    } catch (e: any) {
      dire(`Lecture impossible : ${e?.message ?? e}`, true);
    }
    setOccupe(false);
  };

  const libelleHardware =
    hardwareLu === "(absente)" ? `absente — ${nomType} se comporte en 2026` : hardwareLu || "…";
  const libelleBatterie =
    batterieLue === "(absente)"
      ? "absente — une batterie est présente (cas normal)"
      : batterieLue === "0"
      ? "0 — aucune batterie, carte sur câble"
      : batterieLue === "1"
      ? "1 — une batterie est présente"
      : batterieLue || "…";

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Réglages NVS, par Bluetooth</IonCardTitle>
        <IonCardSubtitle>
          Pose la variable <b>HARDWARE</b> — carte 2026 ou tout-en-un 2027 — et, pour une cible, la
          variable <b>BATTERIE</b>. À faire une fois par carte, à la mise en service. Une carte sans
          HARDWARE se comporte en 2026 ; une cible sans BATTERIE est considérée avec batterie.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        {!appareil ? (
          <>
            <IonSegment
              value={type}
              disabled={occupe}
              onIonChange={(e) => setType(e.detail.value as Appareil)}
            >
              <IonSegmentButton value="cible">
                <IonLabel>Cible</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="carabine">
                <IonLabel>Carabine</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {type === "carabine" && (
              <IonText color="warning">
                <p>
                  Mettez d'abord la carabine <b>en attente OTA</b> (écran « Pair/OTA ») : c'est le
                  seul moment où elle allume son Bluetooth. Il faut une carabine 3.08 ou plus récente.
                </p>
              </IonText>
            )}

            <IonButton onClick={connecter} disabled={occupe}>
              Connecter {type === "cible" ? "une cible" : "une carabine"}
            </IonButton>
          </>
        ) : (
          <>
            <IonText>
              <p>
                Connecté à <b>{appareil.name ?? appareil.deviceId}</b>
                <br />
                Firmware : <b>{versionFw || "…"}</b>
                <br />
                HARDWARE en NVS : <b>{libelleHardware}</b>
                {type === "cible" && (
                  <>
                    <br />
                    BATTERIE en NVS : <b>{libelleBatterie}</b>
                  </>
                )}
              </p>
            </IonText>

            <IonItem lines="none">
              <IonLabel>Modèle de carte</IonLabel>
            </IonItem>
            <IonSegment
              value={modele}
              disabled={occupe}
              onIonChange={(e) => setModele(e.detail.value as Modele)}
            >
              <IonSegmentButton value="2026">
                <IonLabel>2026</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="2027">
                <IonLabel>2027 tout-en-un</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            <IonButton
              onClick={() => graver(`HARDWARE${modele}`, `HARDWARE = ${modele}`)}
              disabled={occupe}
            >
              Appliquer HARDWARE = {modele}
            </IonButton>

            {type === "cible" && (
              <>
                <IonItem lines="none">
                  <IonLabel class="ion-text-wrap">
                    Batterie montée ? Ne répondez « non » que pour une cible 2027 qu'on fait tourner
                    sur câble, sans accu.
                  </IonLabel>
                </IonItem>
                <IonSegment
                  value={batterie}
                  disabled={occupe}
                  onIonChange={(e) => setBatterie(e.detail.value as Batterie)}
                >
                  <IonSegmentButton value="1">
                    <IonLabel>Oui</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="0">
                    <IonLabel>Non, sur câble</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
                <IonButton
                  onClick={() =>
                    graver(`BATTERIE${batterie}`, `BATTERIE = ${batterie === "1" ? "oui" : "non"}`)
                  }
                  disabled={occupe}
                >
                  Appliquer BATTERIE = {batterie === "1" ? "oui" : "non"}
                </IonButton>
              </>
            )}

            <IonButton fill="outline" onClick={lireInventaire} disabled={occupe}>
              Voir toutes les variables NVS
            </IonButton>

            <IonButton color="medium" onClick={deconnecter} disabled={occupe}>
              Déconnecter
            </IonButton>
          </>
        )}

        {message && (
          <IonText color={erreur ? "danger" : "success"}>
            <p>{message}</p>
          </IonText>
        )}

        {inventaire && (
          <>
            <IonText color="medium">
              <p>Espace « parametres » :</p>
            </IonText>
            <pre className="ota-journal">{inventaire}</pre>
          </>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default NvsTechnicien;
